// The FALLBACK paths of the Healix referral body: the names the Healix spec
// (v1.2) gives its fields, accepted for a caller that cannot yet send ours.
// Healix's release cycle is too long to remap its export within (PPL-3680), so
// each of its paths falls back onto the field it means. There is still ONE
// referral schema: the fallback paths are read into ours while parsing,
// exactly as the DDMMYYYY date of birth and the diagnosis strings are
// (docs/Schemas.md: the body is read by the schema).
//
// Only the values that move BETWEEN objects live here. An alias that stays
// inside one object is read by that object's own schema:
//   `patient.memberId`  -> ReferralHealixPatientSchema   (patient.externalId)
//   `patient.product`   -> ReferralHealixPatientSchema   (patient.tenantProductId)
//   `diagnosis.details` -> ReferralHealixDiagnosisSchema (diagnosis.conditions)
//
// Two rules hold throughout:
//  - our own name always wins: a fallback is only read where the body did not
//    state the field itself, so a caller half way through the migration is
//    never overridden by the path it has already left behind;
//  - the function is idempotent, because the referral endpoint applies it to
//    the body before it prepares the documents and settles the consent, and
//    the create schema applies it again while parsing.
import {
  CoverageRole,
  ReferralConsentEvidence,
  ReferralConsentStatus,
} from '../../enums';

type PlainObject = Record<string, unknown>;

const isPlainObject = (value: unknown): value is PlainObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// The two roles as the Healix spec (v1.2) spells them — the same as
// CoverageRole — and as the Healix system turned out to send them when it was
// tested against the API (PPL-3889): capitalised, and "Principle" for the
// policy holder. The spelling of the value is Healix's to choose and takes a
// release of theirs to change, so each one is read as the role it means;
// anything else travels across as sent, for the coverage schema to reject.
const healixMemberTypeRoles: Record<string, CoverageRole> = {
  principal: CoverageRole.principal,
  principle: CoverageRole.principal,
  dependant: CoverageRole.dependant,
  dependent: CoverageRole.dependant,
};

const readHealixMemberType = (memberType: unknown): unknown =>
  typeof memberType === 'string'
    ? (healixMemberTypeRoles[memberType.trim().toLowerCase()] ?? memberType)
    : memberType;

// Healix `patient.memberType` — the role the member holds on the policy, which
// we keep on the referral's own `coverage` next to the subscriber it belongs
// to, where it is validated the way every other caller's is.
const readHealixCoverage = (referral: PlainObject): PlainObject => {
  const { patient } = referral;
  if (!isPlainObject(patient) || patient.memberType === undefined) {
    return referral;
  }

  const { memberType: sentMemberType, ...rest } = patient;
  const memberType = readHealixMemberType(sentMemberType);

  // a coverage sent as something other than an object is left as it came, for
  // the schema to reject it
  if (referral.coverage !== undefined && !isPlainObject(referral.coverage)) {
    return { ...referral, patient: rest };
  }

  const coverage = referral.coverage as PlainObject | undefined;
  return {
    ...referral,
    patient: rest,
    coverage: { ...coverage, role: coverage?.role ?? memberType },
  };
};

// Healix `patient.callBeforeEmail` — the tick box asking us to telephone the
// member before anything is sent to them. It is one of the three ways a
// referral's consent can stand (ReferralConsentSchema), so it is read as that
// rather than kept as a flag of its own.
//
// Only `true` says something: it is the caller telling us the consent is NOT
// in hand and that we are to obtain it by telephone. `false` is the absence of
// that request, which leaves the consent to the endpoint's default — referring
// through the API IS the attestation (see postHealixReferralInterSystem) — so
// it must not be turned into a consent statement here, or the referral forms
// that share this schema would have their question answered for them.
const readHealixConsent = (referral: PlainObject): PlainObject => {
  const { patient } = referral;
  if (!isPlainObject(patient) || patient.callBeforeEmail === undefined) {
    return referral;
  }

  const { callBeforeEmail, ...rest } = patient;

  const asksForCall = callBeforeEmail === true || callBeforeEmail === 'true';
  if (!asksForCall || referral.consent !== undefined) {
    return { ...referral, patient: rest };
  }

  return {
    ...referral,
    patient: rest,
    consent: {
      patient: {
        referral: {
          evidence: ReferralConsentEvidence.phoneCall,
          status: ReferralConsentStatus.pending,
        },
      },
    },
  };
};

// Healix `diagnosis.joinReasonId` — why the member is being referred, which is
// a property of the referral and not of the diagnosis (a carer has none).
const readHealixJoinReason = (referral: PlainObject): PlainObject => {
  const { diagnosis } = referral;
  if (!isPlainObject(diagnosis) || diagnosis.joinReasonId === undefined) {
    return referral;
  }

  const { joinReasonId, ...rest } = diagnosis;
  return {
    ...referral,
    diagnosis: rest,
    joinReasonId: referral.joinReasonId ?? joinReasonId,
  };
};

// The fallback names of a document's two fields, `fileName` and
// `fileContentBase64`; `type` Healix already calls what we call it.
const isHealixFallbackDocument = (document: unknown): document is PlainObject =>
  isPlainObject(document) &&
  (document.fileName !== undefined || document.fileContentBase64 !== undefined);

const readHealixDocument = (document: unknown) => {
  if (!isHealixFallbackDocument(document)) {
    return document;
  }

  const { fileName, fileContentBase64, ...rest } = document;
  const read: PlainObject = { ...rest };
  if (fileName !== undefined && read.name === undefined) {
    read.name = fileName;
  }
  if (fileContentBase64 !== undefined && read.contentBase64 === undefined) {
    read.contentBase64 = fileContentBase64;
  }

  return read;
};

const readHealixDocuments = (referral: PlainObject): PlainObject => {
  const { documents } = referral;
  if (!Array.isArray(documents) || !documents.some(isHealixFallbackDocument)) {
    return referral;
  }

  return { ...referral, documents: documents.map(readHealixDocument) };
};

// A referral body as Healix sends it, with every fallback path read into the
// field the referral schema declares. Anything that is not an object is left
// alone for the schema to report.
export const readHealixFallbackReferral = (body: unknown): unknown => {
  if (!isPlainObject(body)) {
    return body;
  }

  return readHealixDocuments(
    readHealixJoinReason(readHealixConsent(readHealixCoverage(body))),
  );
};
